// Session introspection — wraps GET /me.
//
// MeResponse mirrors the backend's `MeResponse` shape (apps/backend/
// src/auth/me.ts), which is `AuthData` plus `environmentName`. We
// redeclare it here rather than importing from @citycare/backend —
// the monorepo dependency rules forbid a frontend→backend edge
// (AGENTS.md §Monorepo Structure). The role literal is kept in
// lockstep manually with packages/shared/src/constants/user-role.ts;
// a drift would be caught in backend contract tests long before it
// reached this surface.

import { apiFetch } from './api'

export type UserRole = 'field_staff' | 'admin'

export interface MeResponse {
  userID: string
  entraOid: string
  role: UserRole
  displayName: string
  /**
   * True when the signed-in user is a direct member of the field-staff
   * Entra group, even if they hold the `admin` role (a user in both
   * groups resolves to admin but is still a field member).
   * RoleHomeRedirect uses this to send dual-group admins to the capture
   * home instead of the admin dashboard.
   *
   * Optional + treated as false when absent: sessions minted before
   * this field shipped (and older test fixtures) behave as a pure admin
   * until the user re-authenticates. Mirrors the backend AuthData field
   * in apps/backend/src/auth/handler.ts.
   */
  isFieldStaffMember?: boolean
  // Encore environment name (e.g. 'local', 'dev', 'staging',
  // 'production'). Sourced from `appMeta().environment.name` on the
  // backend so client-side affordances can gate on it without baking
  // env knowledge into the build. See ADR-012.
  //
  // Optional on the frontend type so test fixtures predating this
  // field don't have to be retrofitted everywhere; the backend
  // populates it on every /me response. Treat `undefined` as "do not
  // show env-gated UI" (defence in depth).
  environmentName?: string
  /**
   * Unix milliseconds when the underlying session cookie expires.
   * Sourced from the JWT `exp` on the backend (or a synthetic
   * `now + SESSION_MAX_AGE_SECONDS` for the local-dev bypass).
   * Used by auth-cache to reject offline cache fallback once the
   * real session has elapsed — see Codex P1 on PR #384.
   *
   * Optional only because legacy test fixtures predating this
   * field don't carry it; the backend always populates it. The
   * cache treats a missing value as "no boundary known" and falls
   * back to the AUTH_CACHE_MAX_AGE_MS ceiling alone.
   */
  sessionExpiresAt?: number
}

export function fetchMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>('/me')
}
